import React, { useState, useEffect, useRef } from 'react';
import axios from '../axiosConfig';

/**
 * 지반응답해석 실시간 진행 상황 모달
 *
 * Server-Sent Events를 통해 실시간 stdout을 표시
 */
const AnalysisProgressModal = ({ boreholeIds, onComplete, onClose }) => {
  const [logs, setLogs] = useState([]);
  const [isComplete, setIsComplete] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [outputDir, setOutputDir] = useState(null);
  const logEndRef = useRef(null);
  const eventSourceRef = useRef(null);
  // React 18 StrictMode 는 개발 모드에서 effect 를 두 번 실행한다.
  // 가드가 없으면 지반응답해석이 두 번 시작되어 서버 부하가 2배가 되고,
  // 결과 디렉토리도 두 벌 생긴다. 실행은 한 번만 되도록 막는다.
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return undefined;
    startedRef.current = true;

    startAnalysis();

    // Cleanup on unmount
    const source = eventSourceRef.current;
    return () => {
      if (source) {
        source.close();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // 자동 스크롤
    if (logEndRef.current) {
      logEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [logs]);

  const startAnalysis = async () => {
    try {
      // EventSource로 SSE 연결
      const url = `${axios.defaults.baseURL}/seismic/ground-response-analysis`;
      const token = localStorage.getItem('token');

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ borehole_ids: boreholeIds })
      });

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.substring(6);

            if (data.startsWith('[COMPLETE]')) {
              setIsComplete(true);
              setLogs(prev => [...prev, { type: 'success', text: data }]);
            } else if (data.startsWith('[ERROR]')) {
              setHasError(true);
              setLogs(prev => [...prev, { type: 'error', text: data }]);
            } else if (data.startsWith('[OUTPUT_DIR]')) {
              const dir = data.substring(13).trim();
              setOutputDir(dir);
              setLogs(prev => [...prev, { type: 'info', text: `출력 디렉토리: ${dir}` }]);
            } else if (data.trim()) {
              setLogs(prev => [...prev, { type: 'log', text: data }]);
            }
          }
        }
      }
    } catch (error) {
      console.error('Analysis error:', error);
      setHasError(true);
      setLogs(prev => [...prev, { type: 'error', text: `오류: ${error.message}` }]);
    }
  };

  const handleViewResults = () => {
    if (outputDir) {
      onComplete(outputDir);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[2000] p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-gray-800">
              지반응답해석 실행 중
            </h2>
            {isComplete && (
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
              >
                ×
              </button>
            )}
          </div>
          <p className="text-sm text-gray-600 mt-2">
            분석이 진행되는 동안 기다려주세요...
          </p>
        </div>

        {/* Log Console */}
        <div className="flex-1 overflow-y-auto p-6 bg-gray-900 font-mono text-sm">
          {logs.map((log, index) => (
            <div
              key={index}
              className={`mb-1 ${
                log.type === 'error'
                  ? 'text-red-400'
                  : log.type === 'success'
                  ? 'text-green-400'
                  : log.type === 'info'
                  ? 'text-yellow-400'
                  : 'text-gray-300'
              }`}
            >
              {log.text}
            </div>
          ))}
          {!isComplete && !hasError && (
            <div className="flex items-center space-x-2 text-blue-400">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-400"></div>
              <span>실행 중...</span>
            </div>
          )}
          <div ref={logEndRef} />
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
          {hasError ? (
            <button
              onClick={onClose}
              className="px-6 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
            >
              닫기
            </button>
          ) : isComplete ? (
            <>
              <button
                onClick={onClose}
                className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                닫기
              </button>
              <button
                onClick={handleViewResults}
                className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
              >
                분석 결과 확인하기
              </button>
            </>
          ) : (
            <button
              disabled
              className="px-6 py-2 bg-gray-300 text-gray-500 rounded-lg cursor-not-allowed"
            >
              실행 중...
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AnalysisProgressModal;
